import OrderDTO from "@/dto/order/OrderDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import OrderStatus from "@/enum/OrderStatus";
import OrderTag from "@/enum/OrderTag";
import ShopType from "@/enum/ShopType";
import { useGlobalStore } from "@/store/hooks";
import { findClosestPendingPeriodPurchaseInfo, getPeriodPurchaseDeliverTypeInfo } from "@/utils/doudian";
import { observer } from "mobx-react-lite";
import moment from "moment";
import React from "react";
import DouDianRecommendLogisticsInfo from "./DouDianRecommendLogisticsInfo";
import { toMillisecondTimestamp } from "@/utils/time";
import { getOrderShippingDeadlineText } from "@/utils/OrderUtils";
import KwaiPromiseArrival from "@/components/KwaiPromiseArrival";

interface ShippingInfoProps {
    order: OrderDTO;

    shop: ShopDTO;
}

export function shouldRenderShippingInfo(order: OrderDTO) {
    return order.status === OrderStatus.PENDING_SHIPMENT;
}

/**
 * 最后发货时间
 */
const ShippingDeadline: React.FC<ShippingInfoProps> = observer(function (props) {
    const { order, shop } = props;

    const globalStore = useGlobalStore();

    if (!order.shippingDeadline) return null;

    const shopSetting = globalStore.shopSettingMapping.get(order.shopId);

    if (shop.type === ShopType.PDD_FULFILLMENT) {
        return (
            <div>
                <span className="mt5 text-danger font-bold small">承诺送达时间:{order.shippingDeadline}</span>
            </div>
        );
    }

    if (!shopSetting) return null;

    const currentTime = moment();

    const { shippingDeadlineWarnHours } = shopSetting;

    if (shippingDeadlineWarnHours === undefined)
        return (
            <div>
                <span className="mt5 text-danger font-bold small">
                    {getOrderShippingDeadlineText(shop.type)}:{order.shippingDeadline}
                </span>
            </div>
        );

    const shippingDeadlineTime = moment(order.shippingDeadline);

    const shippingDeadlineWarnHoursTime = currentTime.add(shippingDeadlineWarnHours, "h");

    if (shippingDeadlineTime.isBefore(shippingDeadlineWarnHoursTime)) {
        return (
            <div>
                <span className="mt5 text-danger font-bold small">
                    {getOrderShippingDeadlineText(shop.type)}:{order.shippingDeadline}
                </span>
            </div>
        );
    }

    return null;
});

const PromisedShippingTime: React.FC<{ order: OrderDTO }> = observer(({ order }) => {
    if (order.promisedShippingTime) {
        return (
            <div>
                <span className="mt5 text-danger font-bold small">
                    【优先发货】承诺发货时间：{order.promisedShippingTime}
                </span>
            </div>
        );
    }

    return null;
});

/** 拼多多预约送达 */
const PddDeliveryAppointmentInfo: React.FC<{ order: OrderDTO }> = observer(({ order }) => {
    if (order.tags?.includes(OrderTag.PDD_DELIVERY_APPOINTMENT)) {
        if (order.pddOrderExtraInfo?.deliveryAppointmentInfo) {
            const pddDeliveryAppointmentInfo = order.pddOrderExtraInfo.deliveryAppointmentInfo;

            return (
                <div>
                    <span className="mt5 text-danger font-bold small">
                        承诺送达时间:{pddDeliveryAppointmentInfo.promiseDeliveryTime} &nbsp;(预约配送时段:&nbsp;
                        {pddDeliveryAppointmentInfo.consumerDeliveryAppointmentDate}&nbsp;
                        {pddDeliveryAppointmentInfo.consumerDeliveryAppointmentInfoDateTime})
                    </span>
                </div>
            );
        }
    }

    return null;
});

/** 拼多多承诺送达信息 */
const PddPromiseArrivalInfo: React.FC<{ order: OrderDTO }> = observer(({ order }) => {
    if (
        order.tags?.includes(OrderTag.PDD_PROMISE_ARRIVAL) &&
        order.pddOrderExtraInfo?.promiseArrivalInfo?.promiseDeliveryTime
    ) {
        const { promiseDeliveryTime } = order.pddOrderExtraInfo.promiseArrivalInfo;
        const promiseDeliveryTimeInfo = moment(promiseDeliveryTime).format("YYYY-MM-DD HH:mm:ss");

        return (
            <div>
                <span className="mt5 text-danger font-bold small">承诺送达时间:{promiseDeliveryTimeInfo}</span>
            </div>
        );
    }

    return null;
});

/** 抖店预约发货信息 */
const DouDianAppointmentShipInfo: React.FC<{ order: OrderDTO }> = observer(({ order }) => {
    if (order.tags?.includes(OrderTag.DOUDIAN_DELIVERY_APPOINTMENT)) {
        if (order.doudianOrderExtraInfo?.appointmentShipTime) {
            return (
                <div>
                    <span className="mt5 text-danger font-bold small">
                        预约发货至:{order.doudianOrderExtraInfo.appointmentShipTime}
                    </span>
                </div>
            );
        }
    }

    return null;
});

/** 抖店承诺日达 */
const DouDianAppointmentArrivalInfo: React.FC<{ order: OrderDTO }> = observer(({ order }) => {
    if (order.tags?.includes(OrderTag.DOUDIAN_APPOINTMENT_ARRIVAL) && order.doudianOrderExtraInfo) {
        const { appointmentArrivalInfo } = order.doudianOrderExtraInfo;

        if (appointmentArrivalInfo?.receiptDate) {
            return (
                <div>
                    <span className="mt5 text-danger font-bold small">
                        承诺送达时间:{appointmentArrivalInfo.receiptDate}
                    </span>
                </div>
            );
        }
    }

    return null;
});

/** 抖店预约发货2.0 */
const DouDianAppointmentDelivery: React.FC<{ order: OrderDTO }> = observer(({ order }) => {
    if (
        order.doudianOrderExtraInfo?.promiseDetail?.promiseAppointmentShipTime &&
        order.tags?.includes(OrderTag.DOUDIAN_APPOINTMENT_DELIVERY)
    ) {
        const formatTime = moment(
            toMillisecondTimestamp(order.doudianOrderExtraInfo.promiseDetail.promiseAppointmentShipTime),
        ).format("YYYY-MM-DD HH:mm");

        return (
            <div>
                <span className="mt5 text-danger font-bold small">预约发货时间:{formatTime}</span>
            </div>
        );
    }

    return null;
});

const DouDianAppointmentArrival: React.FC<{ order: OrderDTO }> = observer(({ order }) => {
    if (
        order.doudianOrderExtraInfo?.promiseDetail?.promiseLatestReceiptTime &&
        order.tags?.includes(OrderTag.DOUDIAN_APPOINTMENT_ARRIVAL_V2)
    ) {
        const formatTime = moment(
            toMillisecondTimestamp(order.doudianOrderExtraInfo.promiseDetail.promiseLatestReceiptTime),
        ).format("YYYY-MM-DD HH:mm");

        return (
            <div>
                <span className="mt5 text-danger font-bold small">预约送达时间:{formatTime}</span>
            </div>
        );
    }

    return null;
});

const DouDianPromiseArrival: React.FC<{ order: OrderDTO }> = observer(({ order }) => {
    if (
        order.doudianOrderExtraInfo?.promiseDetail?.promiseLatestReceiptTime &&
        order.tags?.includes(OrderTag.DOUDIAN_PROMISE_ARRIVAL)
    ) {
        const formatTime = moment(
            toMillisecondTimestamp(order.doudianOrderExtraInfo.promiseDetail.promiseLatestReceiptTime),
        ).format("YYYY-MM-DD HH:mm");

        return (
            <div>
                <span className="mt5 text-danger font-bold small">承诺送达时间:{formatTime}</span>
            </div>
        );
    }

    return null;
});

/** 京东承诺发货时间 */
const JdAppointmentShipInfo: React.FC<{ order: OrderDTO }> = observer(({ order }) => {
    if (order.tags?.includes(OrderTag.JD_DELIVERY_APPOINTMENT) && order.jdOrderExtraInfo) {
        const { deliveryAppointment } = order.jdOrderExtraInfo;

        if (deliveryAppointment?.opPickDate) {
            return (
                <div>
                    <span className="mt5 text-danger font-bold small">
                        预约发货时间 :{deliveryAppointment.opPickDate}
                    </span>
                </div>
            );
        }
    }

    return null;
});

const DouDianPeriodPurchase: React.FC<{ order: OrderDTO }> = observer(({ order }) => {
    if (!order.doudianOrderExtraInfo?.periodPurchaseInfoList?.length) return null;

    const { periodPurchaseInfoList } = order.doudianOrderExtraInfo;
    const latestPeriodPurchaseInfo = findClosestPendingPeriodPurchaseInfo(periodPurchaseInfoList, order.orderItems);

    if (!latestPeriodPurchaseInfo) return null;

    const deliverTypeInfo = getPeriodPurchaseDeliverTypeInfo(latestPeriodPurchaseInfo.deliverType);

    return (
        <div className="small">
            <span className="text-danger fb">
                最新一次{deliverTypeInfo}:{moment(latestPeriodPurchaseInfo.deliverTime).format("YYYY-MM-DD")}
            </span>
            <span className="text-gray ml5">点击详情按钮可查看每次配送时效</span>
        </div>
    );
});

/**
 * 发货信息
 */
const ShippingInfo: React.FC<ShippingInfoProps> = function (props) {
    const { order, shop } = props;

    if (!shouldRenderShippingInfo(order)) {
        return null;
    }

    return (
        <>
            <PromisedShippingTime order={order} />
            <PddDeliveryAppointmentInfo order={order} />
            <PddPromiseArrivalInfo order={order} />
            <DouDianAppointmentShipInfo order={order} />
            <DouDianAppointmentArrivalInfo order={order} />
            <JdAppointmentShipInfo order={order} />
            <DouDianPeriodPurchase order={order} />
            <DouDianAppointmentDelivery order={order} />
            <DouDianAppointmentArrival order={order} />
            <DouDianPromiseArrival order={order} />
            <KwaiPromiseArrival order={order} />
            {order.shippingDeadline ? <ShippingDeadline order={order} shop={shop} /> : null}

            {order.doudianOrderExtraInfo?.promiseDetail?.recommendLogisticsList?.length ? (
                <DouDianRecommendLogisticsInfo order={order} />
            ) : null}
        </>
    );
};

export default observer(ShippingInfo);
